import {api, RequestParam} from "@/shared/api";
import * as Input from "@/shared/utils/input";

import {WorkingTimeRequestStepsConfigurationComponent} from "@/features/requests/workingTimeRequestStepsConfigurationComponent";
import {
	TaskSelectionConfigurationComponent,
	TaskSelectionConfigurationComponentData
} from "@/features/terminal/taskSelectionConfigurationComponent";
import {Combobox} from "@/shared/ui/combobox";
import {ButtonRadioGroup} from "@/shared/ui/buttonRadioGroup";
import {Integrations} from "./integrations";
import {ReactIsland} from "@/shared/reactIsland";
import {
	IntegrationsGeneralPanelProps,
	mountIntegrationsGeneralPanelIsland
} from "./integrationsGeneralPanelIsland";
import {mountSoapApiPanelIsland, SoapApiPanelProps} from "./soapApiPanelIsland";
import {mountApiCredentialsPanelIsland, ApiCredentialsPanelProps} from "./api/apiCredentialsPanelIsland";
import {FormUtil} from "@/shared/forms/formUtil";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {Notify} from "@/shared/ui/notify";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDate, getCurrentTimeZoneId, parseDecimalNumber, Temporal} from "@/shared/utils/datefunctions";
import {
	ClientSettingsModelResponse,
	ErrorResponse,
	PrimaryTab,
	TimeZoneId,
	WorkingTimeRequestStepConfiguration
} from "@/shared/timr";
import {DateTimeChangeableByUserDayRangeMode, RecordingMode} from "@/shared/timrTypes";

/** Frontend-only record type used by the location-settings UI for DOM and message-key suffixes and for selecting the deletion endpoint. */
enum RecordType {
	WorkingTime = "WorkingTime",
	ProjectTime = "ProjectTime",
	DriveLog = "DriveLog"
}

export interface OAuthSettingsConfig {
	oAuthTokenUrl: string;
	timrApiBaseUrl: string;
}

export interface SettingsConfig {
	externalAuthsExist: {[key: string]: boolean};
	currentUserSSOConnections: string[];
	currentUserSamlConnected: boolean;
	externalAuthProviderConnected: boolean;
	timeZoneIds: TimeZoneId[];
	clientTimeZoneId: string;
	oAuthSettings: OAuthSettingsConfig;
	mobileTaskSelectionConfiguration: TaskSelectionConfigurationComponentData
	workingTimeRequestStepsConfiguration: WorkingTimeRequestStepConfiguration[];
	showExternalId: boolean;
	/** `null` for accounts without a SOAP API token — the SOAP API panel stays away then. */
	timrSyncToken: string | null;
	/** The REST API panel stays visible without the feature, but creating/editing credentials is gated on it. */
	apiFeatureAllowed: boolean;
}

export class SettingsPage {
	public static SETTINGS_GROUP = "settingsGroup";

	private static readonly DELETE_GPS_POSITION_FROM_MIN_DAYS = 1;
	private static readonly DELETE_GPS_POSITION_FROM_MAX_DAYS = 365;

	private dom = $("#settingsContainer");
	private settingsChanged = false;
	private workingTimeEnabledUpdated = globals.workingTimeEnabled;
	private timeZoneIdCombobox: Combobox<TimeZoneId>;
	private deleteGPSPositionAutomaticEnabled: Record<RecordType, BooleanConstructor | boolean> = {
		[RecordType.WorkingTime]: Boolean,
		[RecordType.ProjectTime]: Boolean,
		[RecordType.DriveLog]: Boolean
	};

	private userAbsenceCalendarCategoryVacation: boolean;
	private userAbsenceCalendarCategoryPaidTimeOff: boolean;
	private userAbsenceCalendarCategoryUnpaidTimeOff: boolean;
	private userAbsenceCalendarCategorySuspension: boolean;
	private userAbsenceCalendarCategoryHoliday: boolean;
	private userAbsenceCalendarCategoryNotice: boolean;

	private integrations: Integrations;
	private integrationsGeneralPanel?: ReactIsland<IntegrationsGeneralPanelProps>;
	private soapApiPanel?: ReactIsland<SoapApiPanelProps>;
	private apiCredentialsPanel?: ReactIsland<ApiCredentialsPanelProps>;
	private mobileTaskSelectionConfigurationComponent: TaskSelectionConfigurationComponent;
	private workingTimeRequestStepsConfigurationComponent?: WorkingTimeRequestStepsConfigurationComponent;

	private dateTimeChangeableByUserDayRangeModes: {
		name: string,
		buttonRadioGroup: ButtonRadioGroup<DateTimeChangeableByUserDayRangeMode>
	}[] = [];

	constructor(private config: SettingsConfig) {
		this.initMain();

		this.initRequestApprovalEmailPermissionsListeners();
		this.initSettingsChangedListeners();
		this.initSsoTabListeners();
		this.initDateTimeChangeAbleListeners();
		this.initTimeZoneIdListeners();
		this.initAbsenceCalendar();
		this.initPasswordRules();
		this.initIntegrationPanels();
		// accessing the lazy singleton bootstraps the integration panels and binds the "add integration" button
		this.integrations = Integrations.instance;
	}


	private initIntegrationPanels() {
		this.integrationsGeneralPanel = mountIntegrationsGeneralPanelIsland(
			"integrationsGeneralPanelRoot",
			this.config.showExternalId,
			() => this.setSettingsChanged(true)
		);
		this.soapApiPanel = mountSoapApiPanelIsland("soapApiPanelRoot", this.config.timrSyncToken);
		this.apiCredentialsPanel = mountApiCredentialsPanelIsland("apiCredentialsPanelRoot", {
			apiFeatureAllowed: this.config.apiFeatureAllowed,
			oAuthSettings: this.config.oAuthSettings
		});
	}

	private initPasswordRules() {
		const customPasswordRulesSelect = $("#useCustomPasswordRules");
		if (customPasswordRulesSelect.length === 0) {
			// the feature is not enabled, no need to initialize anything
			return;
		}

		customPasswordRulesSelect.change(() => {
			const useCustomPasswordRules = $("#useCustomPasswordRules").val() === "on";
			$("#passwordRuleDetails").css("display", useCustomPasswordRules ? "block" : "none");
		});

		const minimumCharactersNumberInput: HTMLInputElement = document.querySelector("#passwordRuleMinimumCharacters");
		const minimumCharactersSliderInput: HTMLInputElement = document.querySelector("#passwordRuleMinimumCharactersSlider");

		// the following two event listeners ensure that the values for the minimum password length number input and slider are kept in sync
		minimumCharactersNumberInput.addEventListener("input", (event) => {
			minimumCharactersSliderInput.value = (event.target as HTMLInputElement).value;
		});

		minimumCharactersSliderInput.addEventListener("input", (event) => {
			minimumCharactersNumberInput.value = (event.target as HTMLInputElement).value;
		});
	}

	private initMain() {
		this.dom.find("#submitClientSettingsForm").click(e => {
			const form = this.dom.find("#clientSettingsForm");
			const formData: RequestParam[] = form.serializeArray();

			// for some fields we need to transmit the values of disabled inputs
			formData.push(...FormUtil.serializeArrayDisabled(form));

			const saveButton = $(e.target);
			Button.setLoading(saveButton);
			const budgetWarningLevel = this.dom.find("#budgetWarningLevel");
			if (budgetWarningLevel.is(":visible") && parseDecimalNumber(budgetWarningLevel.val() as string, null) === null) {
				BootstrapModal.error({
					title: "dialog.error.title",
					message: "admin.clientSettings.budgetWarningLevel.invalid"
				});
				return;
			}
			const budgetWarningInterval = this.dom.find("#budgetWarningInterval");
			if (budgetWarningInterval.is(":visible") && parseDecimalNumber(budgetWarningInterval.val() as string, null) === null) {
				BootstrapModal.error({
					title: "dialog.error.title",
					message: "admin.clientSettings.budgetWarningInterval.invalid"
				});
				return;
			}

			// remove field of transferred properties to save null in backend if unchecked (state)
			const emailNotificationForWorkingTimeRequestIndex = formData.findIndex(f => f.name === "sendEmailNotificationsForWorkingTimeRequestsToAddress");
			if (!this.dom.find("#email-notification-to-address-checkbox").is(":checked") && emailNotificationForWorkingTimeRequestIndex !== -1) {
				formData.splice(emailNotificationForWorkingTimeRequestIndex, 1);
			}

			// add Mobile Task Selection Configuration to FormData
			const {
				taskSelectionTypes,
				startOnSelection,
				openSearchInAll,
				openAutomatically
			} = this.mobileTaskSelectionConfigurationComponent.getData();
			formData.push({
				name: "mobileTaskSelectionTypes",
				value: $("#mobileTaskSelectionTypesSwitchery").is(":checked") ? taskSelectionTypes.join() : null
			});
			formData.push({name: "mobileStartOnSelection", value: startOnSelection});
			formData.push({name: "mobileOpenSearchInAll", value: openSearchInAll});
			formData.push({name: "mobileOpenAutomatically", value: openAutomatically});

			this.dateTimeChangeableByUserDayRangeModes.forEach(mode => {
				formData.push({name: mode.name, value: mode.buttonRadioGroup.getValue()});
			});

			const endWorkingTimeAt: string = $("#endWorkingTimeAtCheckbox").prop("checked")
				? $("#endWorkingTimeAt").val() as string
				: null;
			formData.push({name: 'endWorkingTimeAt', value: endWorkingTimeAt});

			api("admin/saveClientSettings.json")
				.post(formData)
				.done(response => {
					this.workingTimeEnabledUpdated = this.dom.find("#workingTimeCheckbox").is(":checked");
					this.setSettingsChanged(false);
					Notify.success(messages["admin.clientSettings.saved"]);
					this.updateBackgroundjobsStatus(response);
					this.reloadBackgroundjobsStatus(response);

					this.updateSamlConfigContainer(response);

					// use the content wrapper to correctly mark tab errors instead of the form not containing the nav tabs
					FormUtil.removeAllFeedback(this.dom.find(".content-wrapper"), true);
				})
				.fail((response: ErrorResponse) => {
					if (response.responseJSON?.fieldErrors) {
						// use the content wrapper to correctly mark tab errors instead of the form not containing the nav tabs
						FormUtil.buildShortMessageFeedback(this.dom.find(".content-wrapper"), response.responseJSON.fieldErrors, "DateTimeChangeable", true);
					}
				})
				.always(() => Button.resetLoading(saveButton));
		});

		this.dom.find("#removeLogoButton").click(() => {
			api("admin/removeLogo.json")
				.post()
				.done(() => {
					this.dom.find("#client-logo").attr("src", "/timr/images/logo-missing.png");
					this.dom.find("#removeLogoButton").hide();
					this.dom.find("#client-logo").css("cursor", "default");
					BootstrapModal.info({
						title: "admin.clientSettings.logo.removed.title",
						message: "admin.clientSettings.logo.removed"
					});
				});
		});

		this.dom.find("#submitUploadLogoForm").click(() => {
			if (this.dom.find("#logoFile").val()) {
				const fd = new FormData();
				const inputElement = this.dom.find("#logoFile")[0] as HTMLInputElement;
				fd.append("logoFile", inputElement.files[0]);
				api("admin/uploadLogo.json")
					.options({
						processData: false,
						contentType: false
					})
					.post(fd)
					.done(() => {
						this.dom.find("#client-logo").attr("src", "/timr/admin/logo.html?time=" + Date.now());
						this.dom.find("#removeLogoButton").show();
						BootstrapModal.info({
							title: "admin.clientSettings.logo.upload.title",
							message: "admin.clientSettings.logo.uploaded"
						});
					});
			} else {
				BootstrapModal.warn({
					title: "admin.clientSettings.logo.upload.title",
					message: "admin.clientSettings.logo.upload.missingfile"
				});
			}
		});

		this.dom.find(".customTypeSelect").each((i, e) => {
			this.updateCustomFieldStyling($(e), $(e).val() === "OPTIONS");
		});

		this.dom.find(".customFieldsSettings").each((i, e) => {
			this.updateCustomFieldsWidth($(e));
		});

		this.dom.find(".customTypeSelect").change((e) => {
			this.updateCustomFieldStyling($(e.target), $(e.target).val() === "OPTIONS");
			this.updateCustomFieldsWidth($(e.target).closest(".customFieldsSettings"));
		});

		const recordingModeInfo = this.dom.find("#recordingModeSelectInfo");
		this.dom.find("#recordingModeSelect").change(e => recordingModeInfo.text(messages[`admin.clientSettings.recordingMode.info.${$(e.target).val() as string}`]));

		const newTaskNotificationInfo = this.dom.find("#mobileNewTaskNotificationSelectInfo");
		this.dom.find("#mobileNewTaskNotificationSelect").change(e => newTaskNotificationInfo.text(messages[`admin.clientSettings.mobileNewTaskNotification.info.${$(e.target).val() as string}`]));

		const locationBasedStartStopReminderInfo = this.dom.find("#locationBasedStartStopReminderInfo");
		this.dom.find("#locationBasedStartStopReminder").change(e => locationBasedStartStopReminderInfo.text(messages[`admin.settings.timrApps.locationBasedStartStopReminder.info.${$(e.target).val() as string}`]));

		this.dom.find("#workingTimeCheckbox").change((e) => {
			const workingTimeCheckbox = $(e.target);
			/*
			 * it is important to execute uncheckFeatureWarning() at first in this event handler:
			 * a click on a locked checkbox (only one feature checked) will set the value to false
			 * -> the method resets the value of the checkbox again to true
			 */
			this.uncheckFeatureWarning(workingTimeCheckbox);
			this.updatePrimaryTabOptions();
			this.updateRecordingModeVisibility();
			this.updateShowTimelineVisibility();
			this.updateStrictModeVisibility();

			const workingTimeCheckboxValue = workingTimeCheckbox.is(":checked");
			this.dom.find("#workingTimeNavListItem").toggle(workingTimeCheckboxValue);
			this.dom.find("#projectTimeExistsWorkingTimes").closest(".form-group").toggle(workingTimeCheckboxValue);
			// if the settings change and the validations has not yet been calculated, the information must be invisible
			if (workingTimeCheckboxValue && !this.workingTimeEnabledUpdated) {
				this.dom.find("#validateProjectTimeExistsWorkingTimesInfo").hide();
			}
			if (!workingTimeCheckboxValue) {
				this.dom.find("#workingTimeRequestsPermissions").hide();
				this.dom.find("#workingTimeRequestsCheckbox").prop("checked", false);
			}
			const primaryTab = $("#primaryTab");
			const projectTimeCheckboxValue = this.dom.find("#projectTimeCheckbox").is(":checked");
			if (!workingTimeCheckboxValue && primaryTab.val() === PrimaryTab.WorkingTime) {
				if (projectTimeCheckboxValue) {
					primaryTab.val(PrimaryTab.ProjectTime);
				} else {
					primaryTab.val(PrimaryTab.DriveLog);
				}
			}
			this.dom.find("#timeSheetReportSettings").toggle(workingTimeCheckboxValue);
			this.dom.find("#validationReportSettings").toggle(workingTimeCheckboxValue || projectTimeCheckboxValue);
			this.dom.find("#presenceBoardSettings").toggle(workingTimeCheckboxValue);
		});

		this.dom.find("#workingTimeRequestsCheckbox").change((e) => {
			const workingTimeRequestsCheckbox = $(e.target);
			const workingTimeRequestsActive = workingTimeRequestsCheckbox.is(":checked");
			const workingTimeActive = this.dom.find("#workingTimeCheckbox").is(":checked");

			this.dom.find("#workingTimeRequestsPermissions").toggle(workingTimeActive && workingTimeRequestsActive);
			if (workingTimeRequestsActive && !workingTimeActive) {
				workingTimeRequestsCheckbox.prop("checked", false);
				BootstrapModal.warn({
					message: "admin.settings.featureCheckboxLockInfo.workingTimeRequest",
					title: "admin.settings.features"
				});
			}
		});

		this.dom.find("#projectTimeCheckbox").change((e) => {
			const projectTimeCheckbox = $(e.target);

			// check the comment for uncheckFeatureWarning() at the #workingTimeCheckbox (same procedure)
			this.uncheckFeatureWarning(projectTimeCheckbox);
			this.updatePrimaryTabOptions();
			this.updateRecordingModeVisibility();
			this.updateShowTimelineVisibility();
			this.updateStrictModeVisibility();
			const projectTimeCheckboxValue = projectTimeCheckbox.is(":checked");
			this.dom.find("#projectTimeNavListItem").toggle(projectTimeCheckboxValue);
			this.dom.find("#projectTimeReportSettings").toggle(projectTimeCheckboxValue);
			this.dom.find("#presenceBoardShowProjectTimeFormGroup").toggle(projectTimeCheckboxValue);

			const primaryTab = this.dom.find("#primaryTab");
			const workingTimeCheckboxValue = this.dom.find("#workingTimeCheckbox").is(":checked");
			if (!projectTimeCheckboxValue && primaryTab.val() === PrimaryTab.ProjectTime) {
				if (workingTimeCheckboxValue) {
					primaryTab.val(PrimaryTab.WorkingTime);
				} else {
					primaryTab.val(PrimaryTab.DriveLog);
				}
			}
			this.dom.find("#validationReportSettings").toggle(workingTimeCheckboxValue || projectTimeCheckboxValue);
		});

		this.dom.find("#driveLogCheckbox").change((e) => {
			const driveLogCheckbox = $(e.target);

			// check the comment for updateCheckboxFeatureLock() at the #workingTimeCheckbox event handler (same procedure)
			this.uncheckFeatureWarning(driveLogCheckbox);
			this.updatePrimaryTabOptions();

			const driveLogCheckboxValue = driveLogCheckbox.is(":checked");
			this.dom.find("#driveLogNavListItem").toggle(driveLogCheckboxValue);
			this.dom.find("#driveLogReportSettings").toggle(driveLogCheckboxValue);

			const primaryTab = $("#primaryTab");
			if (!driveLogCheckboxValue && primaryTab.val() === PrimaryTab.DriveLog) {
				if (this.dom.find("#workingTimeCheckbox").is(":checked")) {
					primaryTab.val(PrimaryTab.WorkingTime);
				} else {
					primaryTab.val(PrimaryTab.ProjectTime);
				}
			}
		});

		this.dom.find(".featureCheckboxContainer input[type='checkbox']").change(() => this.updateFunctionSetupHints());
		this.updateFunctionSetupHints();

		this.dom.find("#workingTimeRecordingMobileAllowedCheckbox, #projectTimeRecordingMobileAllowedCheckbox")
			.change(() => this.updateRecordingModeVisibility());
		this.dom.find("#sendWarningMailsSelect").change((e) => {
			this.dom.find("#budgetWarningLimit").toggle($(e.target).is(":checked"));
		});

		this.dom.find("#workingTimeCheck").change((e) => {
			const validateWorkingTimes = $(e.target).is(":checked");
			this.dom.find("#validateWorkingTimeInfo").toggle(validateWorkingTimes && this.dom.find("#workingTimeStatus").text().trim().length > 0);
			this.dom.find("#workingTimeValidationsConfigurationContainer").toggle(validateWorkingTimes);
		});
		this.dom.find("#workingTimeOverlaps").change((e) => {
			this.dom.find("#validateWorkingTimeOverlapsInfo").toggle($(e.target).is(":checked") && this.dom.find("#workingTimeOverlapsStatus").text().trim().length > 0);
		});
		this.dom.find("#projectTimeOverlaps").change((e) => {
			this.dom.find("#validateProjectTimeOverlapsInfo").toggle($(e.target).is(":checked") && this.dom.find("#projectTimeOverlapsStatus").text().trim().length > 0);
		});
		this.dom.find("#projectTimeExistsWorkingTimes").change((e) => {
			this.dom.find("#validateProjectTimeExistsWorkingTimesInfo").toggle($(e.target).is(":checked") && this.dom.find("#projectTimeExistsWorkingTimesStatus").text().trim().length > 0);
		});

		this.toggleWorkingTimeValidationsConfigurationCollapse();
		this.dom.find("#showAllWorkingTimeValidationsSwitchery").click(() => {
			this.toggleWorkingTimeValidationsConfigurationCollapse();
		});

		this.initDeleteGPSPositionMenu(RecordType.WorkingTime);
		this.initDeleteGPSPositionMenu(RecordType.ProjectTime);
		this.initDeleteGPSPositionMenu(RecordType.DriveLog);

		this.dom.find("#email-notification-to-address-checkbox").click(e => {
			const checked = $(e.target).is(":checked");
			const message = messages[`admin.settings.workingTimeRequests.emailNotification.address.${checked ? "placeholder" : "info"}`];

			// set focus in input, if unchecked disable input and empty field and set info text as placeholder
			this.dom.find("#email-notification-to-address-input")
				.attr("placeholder", message)
				.prop("disabled", !checked)
				.val("")
				.focus();
		});

		this.dom.find("#mobileTaskSelectionTypesSwitchery").click(e => {
			this.mobileTaskSelectionConfigurationComponent.changeVisibility($(e.target).is(":checked"));
		});
		this.mobileTaskSelectionConfigurationComponent = document.getElementById("mobileSelectionConfigurationComponent") as TaskSelectionConfigurationComponent;
		this.mobileTaskSelectionConfigurationComponent.config = {
			...this.config.mobileTaskSelectionConfiguration,
			visible: !!this.config.mobileTaskSelectionConfiguration.selectedTaskSelectionTypes
		};

		this.workingTimeRequestStepsConfigurationComponent = document.getElementById("workingTimeRequestStepsConfigurationComponent") as WorkingTimeRequestStepsConfigurationComponent;
		if (this.workingTimeRequestStepsConfigurationComponent) {	// not available if requests are not allowed
			this.workingTimeRequestStepsConfigurationComponent.config = this.config.workingTimeRequestStepsConfiguration;
		}

		this.dom.find("input[name='showCombinedRecording']").change(() => {
			this.dom.find("input[name='showTimeline']").prop("checked", true);
			this.onShowCombinedRecordingChange();
		});

		this.dom.find("#endWorkingTimeAtCheckbox").change(e => {
			$(".toggleEndWorkingTimeAt").toggle($(e.target).is(":checked"))
		});

		this.dom.find("#samlConfigRemoveButton").click(() => {
			this.dom.find("#saml-provider").val("");
			this.dom.find("#saml-metadata").val("");
			this.dom.find("#saml-urls-container").hide();
			this.dom.find("#samlConfigRemoveButton").hide();
			this.settingsChanged = true;
		});
	}

	private initTimeZoneIdListeners() {
		this.timeZoneIdCombobox = new Combobox({
			items: this.config.timeZoneIds,
			formatter: timeZoneId => `${timeZoneId.offset}: ${timeZoneId.displayName} (${timeZoneId.id})`,
			idElement: $("#timeZoneIdFilterId"),
			filterElement: $("#timeZoneIdFilter"),
			caretElement: $("#timeZoneIdFilterCaret"),
			clearable: false
		});

		// set field if client has time zone configured (otherwise it's blank) and update visibility of fill time zone button
		if (this.config.clientTimeZoneId) {
			this.timeZoneIdCombobox.setSelectedItemById(this.config.clientTimeZoneId);
		}
		this.updateVisibilityFillTimeZoneIdButton(this.config.clientTimeZoneId);

		// update visibility of fill time zone button if the selected current time zone has changed
		$(this.timeZoneIdCombobox).on(Combobox.EVENT_CHANGED, (e, timeZoneId: TimeZoneId) => {
			this.updateVisibilityFillTimeZoneIdButton(timeZoneId.id);
		});

		// initialize button to select the browsers current time zone id
		this.dom.find("#fillTimeZoneIdButton").click(() => {
			this.timeZoneIdCombobox.setSelectedItemById(getCurrentTimeZoneId());
		});
	}

	private updateSamlConfigContainer(response: ClientSettingsModelResponse) {
		if (response && typeof response.samlconfigAvailable !== "undefined") {
			this.dom.find("#saml-urls-container").toggle(!!response.samlconfigAvailable);
			this.dom.find("#samlConfigRemoveButton").toggle(!!response.samlconfigAvailable);
		}
	}

	/**
	 * Updates visibility of fill time zone id button (container)
	 * The transmitted timeZoneId is compared with the time zone of the browser
	 * In case of a mismatch the stored one is null the button is visible
	 * @param timeZoneId object containing zoneId to be compared
	 * @private
	 */
	private updateVisibilityFillTimeZoneIdButton(timeZoneId: string) {
		this.dom.find("#fillTimeZoneIdButton")
			.closest(".form-group")
			.toggle(!timeZoneId || timeZoneId !== getCurrentTimeZoneId());
	}

	private updateFunctionSetupHints(): void {
		this.dom.find("[data-function-setup-hint]").each((_, element) => {
			const hint = $(element);
			const checkbox = this.dom.find(hint.attr("data-function-setup-hint"));
			hint.toggle(checkbox.is(":checked"));
		});
	}

	/**
	 * Update the visibility of the timeline feature setting
	 * The changeable checkbox is only displayed if working time is enabled
	 * Otherwise only a dummy checkbox is displayed which is disabled and unchecked
	 */
	private updateShowTimelineVisibility(): void {
		const timelineAvailable = this.dom.find("#workingTimeCheckbox").is(":checked") || this.dom.find("#projectTimeCheckbox").is(":checked");
		this.dom.find("#showTimelineDisabledInfo").toggle(!timelineAvailable);
		this.dom.find("#showTimelineContainer").toggle(timelineAvailable);
	}

	/**
	 * Updates checkbox state and value on a change of the checkbox for show combined recording
	 * @private
	 */
	private onShowCombinedRecordingChange(): void {
		const isShowCombinedRecordingChecked = this.dom.find("input[name='showCombinedRecording']").is(":checked");
		const showTimelineCheckbox = this.dom.find("input[name='showTimeline']");

		// activate timeline if combined recording is enabled and activated
		if (isShowCombinedRecordingChecked) {
			showTimelineCheckbox.prop("checked", true);

			// Initialize strict mode checkbox from recording mode dropdown value
			// This ensures that when turning on combined recording, we honor the previously selected mode
			const currentRecordingMode = this.dom.find("#recordingModeSelect").val() as RecordingMode;
			this.dom.find("#strictModeCheckbox").prop("checked", currentRecordingMode === RecordingMode.STRICT);
		}
		showTimelineCheckbox.prop("disabled", isShowCombinedRecordingChecked);

		this.updateStrictModeVisibility();
		this.updateRecordingModeVisibility();
		this.updateBetaPanelVisibility();

		// Hide primary tab dropdown when combined recording is active
		this.dom.find("#primaryTabFormGroup").toggle(!isShowCombinedRecordingChecked);
	}

	private updateStrictModeVisibility(): void {
		const isShowCombinedRecordingChecked = this.dom.find("input[name='showCombinedRecording']").is(":checked");
		const isWorkingTimeEnabled = this.dom.find("#workingTimeCheckbox").is(":checked");
		const isProjectTimeEnabled = this.dom.find("#projectTimeCheckbox").is(":checked");

		const visible = isShowCombinedRecordingChecked && isWorkingTimeEnabled && isProjectTimeEnabled;
		this.dom.find("#strictModeCheckboxContainer").toggle(visible);
	}

	private updateBetaPanelVisibility(): void {
		const isShowCombinedRecordingChecked = this.dom.find("input[name='showCombinedRecording']").is(":checked");
		// Hide beta panel when combined recording is active (regardless of strict mode)
		this.dom.find("#betaPanel").toggle(!isShowCombinedRecordingChecked);
	}

	/**
	 * Initializes the listeners for each of the three dateTimeChangeable (for wt, pt, dl) within a loop to avoid duplicate code
	 * If trust based wt is allowed there is the option "Changeable by User within x days" and additional inputs if it is selected (days and mode)
	 * Since the values of the individual mode button radio groups (button tag) cannot be stored via the name tag within the form
	 * They are included in an array that is added to the form data before saving
	 * @private
	 */
	private initDateTimeChangeAbleListeners() {
		this.dom.find(".dateTimeChangeableByUserDayRangeContainer").each((i, e) => {
			const container = $(e);
			const formGroup = container.find(".dateTimeChangeableByUserDayRangeFormGroup");
			const savedValidationsEnabled = globals.savedValidationsEnabled;

			// fields for date time changeable days and mode are only visible if "Changeable by User within x days" is selected
			const selects = $(".dateTimeChangeableSelect");
			const currentSelect = container.find(".dateTimeChangeableSelect");
			const form = this.dom.find(".content-wrapper");
			currentSelect.change(s => {
				if ($(s.target).val() === "BY_USER_DAY_RANGE") {
					formGroup.toggle(savedValidationsEnabled);
					if (!savedValidationsEnabled) {
						FormUtil.markTabFieldErrors(currentSelect, form);
					}
				} else {
					if (!savedValidationsEnabled && !FormUtil.hasTabFeedback(currentSelect, form)) {
						FormUtil.removeTabFeedback(form, currentSelect);
					}
					formGroup.hide();
				}
				Input.toggle($("#submitClientSettingsForm"), !selects.toArray().some((e) => $(e).val() === "BY_USER_DAY_RANGE" && !savedValidationsEnabled));
			});

			// add each date time changeable mode button radio group and name to the array
			this.dateTimeChangeableByUserDayRangeModes.push({
				name: container.find("#dateTimeChangeableByUserDayRangeModeButtonGroup").attr("aria-label"),
				buttonRadioGroup: ButtonRadioGroup.typed({
					value: DateTimeChangeableByUserDayRangeMode.WARNING,
					// use e.querySelector to get the button element from the container
					// because button radio groups expects a HTMLElement instead of JQuery object
					button: e.querySelector(".dateTimeChangeableByUserDayRangeWarningButton")
				}, {
					value: DateTimeChangeableByUserDayRangeMode.PREVENT_EDIT,
					// use e.querySelector to get the button element from the container
					// because button radio groups expects a HTMLElement instead of JQuery object
					button: e.querySelector(".dateTimeChangeableByUserDayRangePreventEditButton")
				}).onValueChanged(mode => {
					// info is only visible if "Show warning" is selected
					container.find(".input-info-text").toggle(mode === DateTimeChangeableByUserDayRangeMode.WARNING);
				})
			});
		});
	}

	private initAbsenceCalendar() {
		this.userAbsenceCalendarCategoryVacation = BrowserStorageSettings.getAbsenceCalendarVacation(SettingsPage.SETTINGS_GROUP);
		this.userAbsenceCalendarCategoryPaidTimeOff = BrowserStorageSettings.getAbsenceCalendarPaidTimeOff(SettingsPage.SETTINGS_GROUP);
		this.userAbsenceCalendarCategoryUnpaidTimeOff = BrowserStorageSettings.getAbsenceCalendarUnpaidTimeOff(SettingsPage.SETTINGS_GROUP);
		this.userAbsenceCalendarCategorySuspension = BrowserStorageSettings.getAbsenceCalendarSuspension(SettingsPage.SETTINGS_GROUP);
		this.userAbsenceCalendarCategoryHoliday = BrowserStorageSettings.getAbsenceCalendarHoliday(SettingsPage.SETTINGS_GROUP);
		this.userAbsenceCalendarCategoryNotice = BrowserStorageSettings.getAbsenceCalendarNotice(SettingsPage.SETTINGS_GROUP);
		this.updateTeammateAbsenceCalendarCategories();
	}

	private initSsoTabListeners() {
		// only bind sso listener if an external auth provider is connected
		if (!this.config.currentUserSamlConnected) {
			if (this.config.externalAuthProviderConnected) {
				this.dom.find("input[name='selectedSSO']").change(this.onSelectedSSOChange);
			}
			this.dom.find("input[name='externalAuthOnly']").change(this.onExternalAuthOnlyChange);
		}
	}

	/**
	 * Handles the actions for the externalAuthOnly change listener
	 * There two cases where activating this checkbox isn't allowed (prevented by showing an error dialog)
	 * 		- no directory connected
	 * 		- current user isn't connected to the provider
	 * Note that we have to remember the settingsChanged flag (it's excluded in registerSettingsChangedListeners)
	 * @param e
	 */
	private onExternalAuthOnlyChange = (e: JQuery.TriggeredEvent) => {
		const externalAuthOnly = $(e.target);
		if (externalAuthOnly.is(":checked")) {
			let error = "";
			if (!this.config.externalAuthProviderConnected) {
				error = "admin.settings.sso.deactivateExternalAuthOnly.noDirectory.message";
			} else if (!this.currentUserHasExternalAuthConnection()) {
				error = "admin.settings.sso.deactivateExternalAuthOnly.unconnected.message";
			}
			if (error) {
				BootstrapModal.warn({
					title: "admin.settings.sso.deactivateExternalAuthOnly.title",
					message: error
				});
				externalAuthOnly.prop("checked", false);
			}
		} else {
			this.settingsChanged = true;
		}
	}

	/**
	 * Handles the actions for SSO change listener
	 * If external auths exist, for the provider that is deselected, there are two cases about externalAuthOnly:
	 * 		- active: the provider must not be disabled
	 *		- inactive: a confirm dialog appears, which must be confirmed
	 * Note that we have to remember the settingsChanged flag (it's excluded in registerSettingsChangedListeners)
	 * @param e
	 */
	private onSelectedSSOChange = (e: JQuery.TriggeredEvent) => {
		const sso = $(e.target);
		// if the option gets unchecked, it will be checked if there are no external auths for the selected option
		if (!sso.is(":checked")) {
			// check the appropriate flag for the selected sso provider
			const ssoProvider = Object.keys(this.config.externalAuthsExist).find(key => key === sso.val());
			if (this.config.externalAuthsExist[ssoProvider]) {
				// after the event is triggered, the checkbox is already unchecked, so it must be checked again
				sso.prop("checked", true);

				// if externalAuthOnly is true, a warning is displayed that it is not possible to disable it
				if (this.dom.find("input[name='externalAuthOnly']").is(":checked")) {
					BootstrapModal.warn({
						title: "admin.settings.sso.deactivateConfirm.title",
						message: "admin.settings.sso.deactivateConfirm.externalAuthOnly.message"
					});
				} else {
					// otherwise a confirmation dialog about unchecking the checkbox is displayed
					BootstrapModal.confirm({
						title: "admin.settings.sso.deactivateConfirm.title",
						message: "admin.settings.sso.deactivateConfirm.message",
						yesLabel: "confirmation.ok",
						noLabel: "confirmation.cancel",
						yesAction: modal => {
							sso.prop("checked", false);
							this.settingsChanged = true;
							modal.close();
						}
					});
				}
			}
		} else {
			this.settingsChanged = true;
		}
	}

	/**
	 * Returns true if an external authorization connection exists under the following conditions:
	 * - at least one sso is selected and the corresponding selectedSSO checkbox is active
	 * - or the user has a saml connection
	 * @private
	 */
	private currentUserHasExternalAuthConnection(): boolean {
		const selectedSSO: string[] = [];
		this.dom.find("input[name='selectedSSO']:checked").each((i, ele) => {
			selectedSSO.push($(ele).val() as string);
		});
		return this.config.currentUserSamlConnected || selectedSSO.some(r => this.config.currentUserSSOConnections.indexOf(r) >= 0);
	}

	private toggleWorkingTimeValidationsConfigurationCollapse() {
		const showAllWorkingTimeValidations = $("#showAllWorkingTimeValidationsSwitchery").is(":checked");
		this.dom.find("#workingTimeValidationsConfigurationCollapse").collapse(showAllWorkingTimeValidations ? "hide" : "show");
	}

	private setSettingsChanged(value: boolean) {
		this.settingsChanged = value;
	}

	private updateBackgroundjobsStatus(response: ClientSettingsModelResponse) {
		const validateWorkingTime = this.dom.find("#workingTimeCheck").is(":checked");
		$("#validateWorkingTimeInfo").toggle(validateWorkingTime &&
			response && response.workingTimeStatus && response.workingTimeStatus !== "CANCELLED");
		$("#workingTimeStatus").text(validateWorkingTime && response?.workingTimeStatus
			? messages["admin.settings.validations.workingTime." + response.workingTimeStatus] : "");
		const workingTimeStatusRunning = response && (response.workingTimeStatus === "RUNNING" || response.workingTimeStatus === "WAITING");
		$("#validateWorkingTimeInfo")
			.toggleClass("input-info-text", !workingTimeStatusRunning)
			.toggleClass("input-warning-text", workingTimeStatusRunning)
			.find(".spinner").toggle(workingTimeStatusRunning);

		const validateWorkingTimeOverlaps = this.dom.find("#workingTimeOverlaps").is(":checked");
		// The async status refresh can return a valid job status even when the frontend message map has no
		// text for that key. In that case, keep the wrapper hidden to avoid rendering only the info icon.
		const workingTimeOverlapsStatusMessage = validateWorkingTimeOverlaps && response?.workingTimeOverlapsStatus
			? messages["admin.settings.validations.overlaps." + response.workingTimeOverlapsStatus] ?? ""
			: "";
		$("#validateWorkingTimeOverlapsInfo").toggle(validateWorkingTimeOverlaps &&
			response && response.workingTimeOverlapsStatus && response.workingTimeOverlapsStatus !== "CANCELLED" &&
			workingTimeOverlapsStatusMessage.trim().length > 0);
		$("#workingTimeOverlapsStatus").text(workingTimeOverlapsStatusMessage);
		const workingTimeOverlapsStatusRunning = response && (response.workingTimeOverlapsStatus === "RUNNING" || response.workingTimeOverlapsStatus === "WAITING");
		$("#validateWorkingTimeOverlapsInfo")
			.toggleClass("input-info-text", !workingTimeOverlapsStatusRunning)
			.toggleClass("input-warning-text", workingTimeOverlapsStatusRunning)
			.find(".spinner").toggle(workingTimeOverlapsStatusRunning);

		const validateProjectTimeOverlaps = this.dom.find("#projectTimeOverlaps").is(":checked");
		$("#validateProjectTimeOverlapsInfo").toggle(validateProjectTimeOverlaps &&
			response && response.projectTimeOverlapsStatus && response.projectTimeOverlapsStatus !== "CANCELLED");
		$("#projectTimeOverlapsStatus").text(validateProjectTimeOverlaps && response?.projectTimeOverlapsStatus
			? messages["admin.settings.validations.overlaps." + response.projectTimeOverlapsStatus] : "");
		const projectTimeOverlapsStatusRunning = response && (response.projectTimeOverlapsStatus === "RUNNING" || response.projectTimeOverlapsStatus === "WAITING");
		$("#validateProjectTimeOverlapsInfo")
			.toggleClass("input-info-text", !projectTimeOverlapsStatusRunning)
			.toggleClass("input-warning-text", projectTimeOverlapsStatusRunning)
			.find(".spinner").toggle(projectTimeOverlapsStatusRunning);

		const validateProjectTimeExistsWorkingTimes = this.dom.find("#projectTimeExistsWorkingTimes").is(":checked");
		$("#validateProjectTimeExistsWorkingTimesInfo").toggle(validateProjectTimeExistsWorkingTimes &&
			response && response.projectTimeExistsWorkingTimesStatus && response.projectTimeExistsWorkingTimesStatus !== "CANCELLED");
		$("#projectTimeExistsWorkingTimesStatus").text(validateProjectTimeExistsWorkingTimes && response?.projectTimeExistsWorkingTimesStatus
			? messages["admin.settings.validations.existsworkingtimes." + response.projectTimeExistsWorkingTimesStatus] : "");
		const projectTimeExistsWorkingTimesStatusRunning = response && (response.projectTimeExistsWorkingTimesStatus === "RUNNING" || response.projectTimeExistsWorkingTimesStatus === "WAITING");
		$("#validateProjectTimeExistsWorkingTimesInfo")
			.toggleClass("input-info-text", !projectTimeExistsWorkingTimesStatusRunning)
			.toggleClass("input-warning-text", projectTimeExistsWorkingTimesStatusRunning)
			.find(".spinner").toggle(projectTimeExistsWorkingTimesStatusRunning);
	}

	private reloadBackgroundjobsStatus(response: ClientSettingsModelResponse) {
		if (response && (
			response.workingTimeStatus === "RUNNING" || response.workingTimeStatus === "WAITING" ||
			response.workingTimeOverlapsStatus === "RUNNING" || response.workingTimeOverlapsStatus === "WAITING" ||
			response.projectTimeOverlapsStatus === "RUNNING" || response.projectTimeOverlapsStatus === "WAITING" ||
			response.projectTimeExistsWorkingTimesStatus === "RUNNING" || response.projectTimeExistsWorkingTimesStatus === "WAITING")) {
			setTimeout(() => {
				api("admin/reloadSettingsBackgroundjobsStatus.json")
					.get()
					.done(data => {
						this.updateBackgroundjobsStatus(data);
						this.reloadBackgroundjobsStatus(data);
					});
			}, 5000);
		}
	}

	private uncheckFeatureWarning(checkboxElement: JQuery) {
		if (this.dom.find(".featureCheckbox:checked").length === 0) {
			checkboxElement.prop("checked", true);
			BootstrapModal.warn({
				message: "admin.settings.featureCheckboxLockInfo",
				title: "admin.settings.features"
			});
		}
	}

	/**
	 * Registers settings changed listener
	 * There are some cases where we want to exclude this or handle this manually with an extra listener
	 * e.g. checkboxes where a warning dialog opens, we need to reset the state initially which breaks the settingsChanged flag
	 * @private
	 */
	private initSettingsChangedListeners() {
		this.dom.find("#clientSettingsForm").find("input, select").change(e => {
			const target = $(e.target);
			if (target.attr("id") !== "logoFile" && target.attr("name") !== "externalAuthOnly" &&
				(!this.config.externalAuthProviderConnected || target.attr("name") !== "selectedSSO")) {
				this.setSettingsChanged(true);
			}
		});

		this.workingTimeRequestStepsConfigurationComponent?.addEventListener(
			WorkingTimeRequestStepsConfigurationComponent.EVENT_CHANGED,
			() => this.setSettingsChanged(true)
		);

		window.onbeforeunload = () => {
			if (this.settingsChanged) {
				return "You will lose all changes made since your last save";
			}
		};
	}

	/**
	 * Shows the info box that admins cannot approve their own requests while showAllUsersForAdmin is disabled (see TIMR-5735)
	 */
	private initRequestApprovalEmailPermissionsListeners() {
		if (SecurityStatus.isCorporateFeaturesAllowed()) {
			this.dom.find("#showAllUsersForAdmin").change(e => {
				const showAllUsersForAdmin = $(e.target).val() === "true";
				this.dom.find("#workingTimeRequestsAdminForItselfInfo").toggle(!showAllUsersForAdmin);
			});
		}
	}

	private updatePrimaryTabOptions() {
		const primaryTabSelect = this.dom.find("#primaryTab");
		const oldValue = primaryTabSelect.val() as string;
		primaryTabSelect.empty();
		if (this.dom.find("#workingTimeCheckbox").is(":checked")) {
			primaryTabSelect.append($("<option></option>", {value: PrimaryTab.WorkingTime}).text(messages["admin.clientSettings.primaryTab.WORKINGTIME"]));
		}
		if (this.dom.find("#projectTimeCheckbox").is(":checked")) {
			primaryTabSelect.append($("<option></option>", {value: PrimaryTab.ProjectTime}).text(messages["admin.clientSettings.primaryTab.PROJECTTIME"]));
		}
		if (this.dom.find("#driveLogCheckbox").is(":checked")) {
			primaryTabSelect.append($("<option></option>", {value: PrimaryTab.DriveLog}).text(messages["admin.clientSettings.primaryTab.DRIVELOG"]));
		}
		primaryTabSelect.append($("<option></option>", {value: PrimaryTab.Automatically}).text(messages["admin.clientSettings.primaryTab.AUTOMATICALLY"]));
		if (oldValue && primaryTabSelect.find(`option[value="${oldValue}"]`).length > 0) {
			primaryTabSelect.val(oldValue);
		} else {
			primaryTabSelect.val(primaryTabSelect.find("option:first").val());
		}
	}

	/**
	 * Updates visibility of mobile recording mode
	 * Mobile recording mode is only visible if working time and project time is enabled and recording allowed for both
	 * AND combined recording is NOT active (when combined recording is active, strict mode checkbox is used instead)
	 * @private
	 */
	private updateRecordingModeVisibility() {
		const visible = this.dom.find("#workingTimeCheckbox:checked, #workingTimeRecordingMobileAllowedCheckbox:checked, " +
			"#projectTimeCheckbox:checked, #projectTimeRecordingMobileAllowedCheckbox:checked").length === 4;
		const isShowCombinedRecordingChecked = this.dom.find("input[name='showCombinedRecording']").is(":checked");
		this.dom.find("#recordingModeSelectFormGroup").toggle(!isShowCombinedRecordingChecked && visible);
	}

	private updateCustomFieldStyling(element: JQuery, optionsFieldActive: boolean) {
		const optionsList = element.parent().find(".optionsList");
		if (optionsFieldActive) {
			optionsList.css("visibility", "visible");
		} else {
			optionsList.css("visibility", "hidden");
		}
		element.toggleClass("value-field-active", optionsFieldActive);
		element.toggleClass("value-field-inactive", !optionsFieldActive);
		optionsList.toggleClass("value-field-active", optionsFieldActive);
		optionsList.toggleClass("value-field-inactive", !optionsFieldActive);
	}

	/*
	 * use the whole width of the panel if no custom field of type options is active
	 * set "display: none" - with toggle() - instead of visibility = hidden
	 */
	private updateCustomFieldsWidth(customFieldsSettings: JQuery) {
		const optionsList = customFieldsSettings.find(".optionsList");

		let value = false;
		// check if a custom field is of type options ("visibility" === "visible")
		optionsList.each((i, e) => {
			if ($(e).css("visibility") === "visible") {
				value = true;
				return false;
			}
		});
		optionsList.toggle(value);
	}

	private updateTeammateAbsenceCalendarCategories() {
		// update teammate absence calendar categories
		// vacation
		$("#userAbsenceCalendarCategoryVacation").change(e => {
			const checked = $(e.target).is(":checked");
			const vacation = $("#teammateAbsenceCalendarCategoryVacation");
			if (!vacation.is(":disabled")) {
				this.userAbsenceCalendarCategoryVacation = vacation.is(":checked");
				BrowserStorageSettings.setAbsenceCalendarVacation(SettingsPage.SETTINGS_GROUP, this.userAbsenceCalendarCategoryVacation);
			}
			vacation
				.prop("disabled", checked)
				.prop("checked", checked || this.userAbsenceCalendarCategoryVacation);
		});
		// paid time off
		$("#userAbsenceCalendarCategoryPaidTimeOff").change(e => {
			const checked = $(e.target).is(":checked");
			const paidTimeOff = $("#teammateAbsenceCalendarCategoryPaidTimeOff");
			if (!paidTimeOff.is(":disabled")) {
				this.userAbsenceCalendarCategoryPaidTimeOff = paidTimeOff.is(":checked");
				BrowserStorageSettings.setAbsenceCalendarPaidTimeOff(SettingsPage.SETTINGS_GROUP, this.userAbsenceCalendarCategoryPaidTimeOff);
			}
			paidTimeOff
				.prop("disabled", checked)
				.prop("checked", checked || this.userAbsenceCalendarCategoryPaidTimeOff);
		});
		// unpaid time off
		$("#userAbsenceCalendarCategoryUnpaidTimeOff").change(e => {
			const checked = $(e.target).is(":checked");
			const unpaidTimeOff = $("#teammateAbsenceCalendarCategoryUnpaidTimeOff");
			if (!unpaidTimeOff.is(":disabled")) {
				this.userAbsenceCalendarCategoryUnpaidTimeOff = unpaidTimeOff.is(":checked");
				BrowserStorageSettings.setAbsenceCalendarUnpaidTimeOff(SettingsPage.SETTINGS_GROUP, this.userAbsenceCalendarCategoryUnpaidTimeOff);
			}
			unpaidTimeOff
				.prop("disabled", checked)
				.prop("checked", checked || this.userAbsenceCalendarCategoryUnpaidTimeOff);
		});
		// suspension
		$("#userAbsenceCalendarCategorySuspension").change(e => {
			const checked = $(e.target).is(":checked");
			const suspension = $("#teammateAbsenceCalendarCategorySuspension");
			if (!suspension.is(":disabled")) {
				this.userAbsenceCalendarCategorySuspension = suspension.is(":checked");
				BrowserStorageSettings.setAbsenceCalendarSuspension(SettingsPage.SETTINGS_GROUP, this.userAbsenceCalendarCategorySuspension);
			}
			suspension
				.prop("disabled", checked)
				.prop("checked", checked || this.userAbsenceCalendarCategorySuspension);
		});
		// holiday
		$("#userAbsenceCalendarCategoryHoliday").change(e => {
			const checked = $(e.target).is(":checked");
			const holiday = $("#teammateAbsenceCalendarCategoryHoliday");
			if (!holiday.is(":disabled")) {
				this.userAbsenceCalendarCategoryHoliday = holiday.is(":checked");
				BrowserStorageSettings.setAbsenceCalendarHoliday(SettingsPage.SETTINGS_GROUP, this.userAbsenceCalendarCategoryHoliday);
			}
			holiday
				.prop("disabled", checked)
				.prop("checked", checked || this.userAbsenceCalendarCategoryHoliday);
		});
		// working time notices
		$("#userAbsenceCalendarCategoryNotice").change(e => {
			const checked = $(e.target).is(":checked");
			const notice = $("#teammateAbsenceCalendarCategoryNotice");
			if (!notice.is(":disabled")) {
				this.userAbsenceCalendarCategoryNotice = notice.is(":checked");
				BrowserStorageSettings.setAbsenceCalendarNotice(SettingsPage.SETTINGS_GROUP, this.userAbsenceCalendarCategoryNotice);
			}
			notice
				.prop("disabled", checked)
				.prop("checked", checked || this.userAbsenceCalendarCategoryNotice);
		});
	}

	private initDeleteGPSPositionMenu(recordType: RecordType) {
		$(`#locationRequirementDeleteManualBtn_${recordType}`).click(() => this.showDeletePositionsDialog(recordType));
		const checkBox = $(`#deleteGPSPositionsAutomaticEnabled_${recordType}`);
		checkBox.change((e) =>
			this.updateDeleteLocationViewButtons(recordType, $(e.target).is(":checked"))
		);
		$(`#olderThenDaysInput${recordType}`).on("input", () =>
			this.updateDeleteLocationViewButtons(recordType, checkBox.is(":checked"))
		);
		this.updateDeleteLocationViewButtons(recordType, checkBox.is(":checked"));
	}

	private updateDeleteLocationViewButtons(recordType: RecordType, automaticDeletionEnabled: boolean) {
		if (this.deleteGPSPositionAutomaticEnabled[recordType] !== automaticDeletionEnabled) {
			this.deleteGPSPositionAutomaticEnabled[recordType] = automaticDeletionEnabled;
			$(`#locationRequirementDeleteInfo_${recordType}`).toggle(automaticDeletionEnabled);
		}
		const days = parseInt($(`#olderThenDaysInput${recordType}`).val() as string, 10) || SettingsPage.DELETE_GPS_POSITION_FROM_MIN_DAYS;
		const infoMsg = $(`#deleteGPSPositionsInfoMsg_${recordType}`);
		infoMsg.text(messages.withArguments(`admin.clientSettings.locationRequirement.delete.info.${recordType}`, days));
	}

	private performDeleteGPSPosition(recordType: RecordType, deleteOlderThanDays: number): JQueryXHR {
		let url: string;
		switch (recordType) {
			case RecordType.WorkingTime:
				url = "admin/deleteGPSPositions/workingTime.json";
				break;
			case RecordType.ProjectTime:
				url = "admin/deleteGPSPositions/projectTime.json";
				break;
			case RecordType.DriveLog:
				url = "admin/deleteGPSPositions/driveLog.json";
				break;
		}
		return api(url).post([{name: "deleteOlderThanDays", value: deleteOlderThanDays}]);
	}

	private showDeletePositionsDialog(recordType: RecordType) {
		const settingsChangedBefore = this.settingsChanged;

		const initValue: number = parseInt($(`#olderThenDaysInput${recordType}`).val() as string, 10) || SettingsPage.DELETE_GPS_POSITION_FROM_MIN_DAYS;
		const dialog = $(`#manualGPSPositionDeletionDialog${recordType}`);
		const olderThanDaysInput = dialog.find(`#olderThanDaysInputDialog${recordType}`).val(initValue);
		const deleteGPSPostionsRadio = dialog.find(`#deleteGPSPostionsRadio${recordType}`);
		const deleteAllGPSPostionsRadio = dialog.find(`#deleteAllGPSPostionsRadio${recordType}`);
		const confirmDeleteTextInput = dialog.find(`#confirmDeleteTextInput${recordType}`).val("");

		// initialize dialog
		deleteGPSPostionsRadio.prop("checked", true);
		deleteAllGPSPostionsRadio.prop("checked", false);
		Input.enable(olderThanDaysInput);
		Input.removeFeedback(confirmDeleteTextInput);
		Input.removeFeedback(olderThanDaysInput);
		this.showDialogInfo(recordType, false);

		// add listeners
		olderThanDaysInput.on("input", () => {
			this.showDialogInfo(recordType, false);
			this.hasDaysInputErrors(olderThanDaysInput, deleteAllGPSPostionsRadio);
		});
		dialog.change(() => {
			const checked = deleteGPSPostionsRadio.is(":checked");
			Input.toggle(olderThanDaysInput, checked);
			if (checked) {
				this.hasDaysInputErrors(olderThanDaysInput, deleteAllGPSPostionsRadio);
			} else {
				Input.removeFeedback(olderThanDaysInput);
			}
			this.showDialogInfo(recordType, !checked);
		});

		// create modal
		const confirm = BootstrapModal.confirm({
			title: `admin.clientSettings.locationRequirement.delete.warning.title.${recordType}`,
			message: dialog,
			destroyOnClose: false,
			escapeText: false,
			yesLabel: "confirmation.delete",
			yesCss: "deleteButton btn-danger",
			preventDoubleClickButtons: true,
			yesAction: (modal, button) => {
				Input.removeFeedback(confirmDeleteTextInput);
				Input.removeFeedback(olderThanDaysInput);

				const confirmCorrect = confirmDeleteTextInput.val() === messages["dialog.confirm.delete.position.comparison"];
				// days === 0 -> delete all positions
				const days = deleteGPSPostionsRadio.is(":checked") ? parseInt(olderThanDaysInput.val() as string, 10) : 0;
				if (confirmCorrect && !this.hasDaysInputErrors(olderThanDaysInput, deleteAllGPSPostionsRadio)) {
					this.performDeleteGPSPosition(recordType, days)
						.done(() => {
							new PNotify({
								text: messages["admin.clientSettings.locationRequirement.delete.success"],
								addclass: "bg-success",
								buttons: {
									sticker: false
								}
							});
						})
						.always(() => {
							Button.resetLoading(button);
							modal.close();
						});
				} else {
					if (!confirmCorrect) {
						Input.addFeedback(confirmDeleteTextInput, "has-error", messages["dialog.confirm.delete.position.error"]);
					}
					setTimeout(() => Button.resetLoading(button), 200);
				}
			},
			noLabel: "confirmation.cancel",
			onClose: () => {
				// Prevent page from asking to save changes if nothing has changed before the dialog got opened
				this.settingsChanged = settingsChangedBefore;
			}
		});
		confirm.getDomModal().find("form").submit(e => e.preventDefault());
	}

	private hasDaysInputErrors(olderThanDaysInput: JQuery, deleteAllGPSPostionsRadio: JQuery): boolean {
		const days = parseInt(olderThanDaysInput.val() as string, 10);
		const daysInsideRange = deleteAllGPSPostionsRadio.is(":checked") || days >= SettingsPage.DELETE_GPS_POSITION_FROM_MIN_DAYS && days <= SettingsPage.DELETE_GPS_POSITION_FROM_MAX_DAYS;
		Input.removeFeedback(olderThanDaysInput);
		if (isNaN(days)) {
			Input.addFeedback(olderThanDaysInput, "has-error", messages["saveClientSettings.daysRequired"]);
			return true;
		} else if (!daysInsideRange) {
			Input.addFeedback(olderThanDaysInput,
				"has-error",
				messages.withArguments("saveClientSettings.daysRange", SettingsPage.DELETE_GPS_POSITION_FROM_MIN_DAYS, SettingsPage.DELETE_GPS_POSITION_FROM_MAX_DAYS)
			);
			return true;
		} else {
			return false;
		}
	}

	private showDialogInfo(recordType: RecordType, showDeleteAllMsg: boolean) {
		const infoMsg = $(`#deleteGPSPositionsInfoMsgDialog_${recordType}`);
		if (showDeleteAllMsg) {
			infoMsg.text(
				messages[`admin.clientSettings.locationRequirement.delete.info.manual.all.${recordType}`]
			);
		} else {
			const days = parseInt($(`#olderThanDaysInputDialog${recordType}`).val() as string, 10) || SettingsPage.DELETE_GPS_POSITION_FROM_MIN_DAYS;
			infoMsg
				.text(
					messages.withArguments(`admin.clientSettings.locationRequirement.delete.info.manual.${recordType}`,
						days,
						formatDate(Temporal.Now.plainDateISO().subtract({days: days}))
					)
				);
		}
	}
}
